import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Headphones, ChefHat, Users, Camera, Flower2, Store, Utensils, Music,
  ArrowRight, CheckCircle2, DollarSign, Calendar, Sparkles, BarChart3,
  ShieldCheck, Clock, Zap,
} from 'lucide-react';

const PARTNER_TYPES = [
  {
    key: 'dj',
    icon: Headphones,
    label: 'DJs & Entertainers',
    color: 'from-fuchsia-500 to-purple-600',
    role: 'vendor',
    firstServicePath: '/vendor',
    sample: 'Weekly slots. Direct bookings. No commission on tips.',
  },
  {
    key: 'caterer',
    icon: ChefHat,
    label: 'Caterers & Chefs',
    color: 'from-amber-500 to-orange-600',
    role: 'vendor',
    firstServicePath: '/vendor',
    sample: 'Menu-level pricing, gallery showcase, drop-off & full-service.',
  },
  {
    key: 'staff',
    icon: Users,
    label: 'Event Staffing',
    color: 'from-indigo-500 to-blue-600',
    role: 'client',
    firstServicePath: '/staffing/my-services',
    sample: 'Full-crew agencies & solo pros. Weekly florist/gala gigs.',
  },
  {
    key: 'photographer',
    icon: Camera,
    label: 'Photo & Video',
    color: 'from-emerald-500 to-teal-600',
    role: 'vendor',
    firstServicePath: '/vendor',
    sample: 'Package tiers, portfolio carousel, same-week turn-around.',
  },
  {
    key: 'florist',
    icon: Flower2,
    label: 'Florists & Decor',
    color: 'from-pink-500 to-rose-600',
    role: 'vendor',
    firstServicePath: '/vendor',
    sample: 'Theme bundles, rentable arches & centerpieces.',
  },
  {
    key: 'rental',
    icon: Store,
    label: 'Rental Suppliers',
    color: 'from-sky-500 to-cyan-600',
    role: 'vendor',
    firstServicePath: '/vendor/inventory',
    sample: 'Chairs, tables, tents, AV — priced per unit, auto-inventoried.',
  },
  {
    key: 'chef',
    icon: Utensils,
    label: 'Private Chefs',
    color: 'from-red-500 to-rose-600',
    role: 'vendor',
    firstServicePath: '/vendor',
    sample: 'Per-head pricing, dietary filters, premium bookings.',
  },
  {
    key: 'band',
    icon: Music,
    label: 'Musicians & Bands',
    color: 'from-violet-500 to-indigo-600',
    role: 'vendor',
    firstServicePath: '/vendor',
    sample: 'Set-length pricing, audio previews, rider uploads.',
  },
];

const PERKS = [
  { icon: DollarSign, label: 'Keep 100% of your rate', sub: 'Transparent buyer-absorbs fee model' },
  { icon: Calendar, label: 'Weekly recurring gigs', sub: 'Partnered events + public marketplace' },
  { icon: BarChart3, label: 'Live demand dashboard', sub: 'See which dates are hot before bidding' },
  { icon: ShieldCheck, label: 'Escrow-backed payouts', sub: 'Funds held until the event, released on completion' },
  { icon: Zap, label: 'Listed on 3 surfaces', sub: 'Marketplace + Event Planning Hub + Supplier Hub' },
  { icon: Clock, label: '30-sec signup', sub: 'Add your first service in under 2 minutes' },
];

const STEPS = [
  { n: 1, title: 'Tell us who you are', desc: 'Pick your partner type and create a free account.' },
  { n: 2, title: 'Add your first service', desc: 'Name it, price it, upload a photo. Two minutes, done.' },
  { n: 3, title: 'Get booked', desc: 'Clients hire you across Eventism Events, the Planning Hub, and the Supplier Hub.' },
];

export default function PartnersLanding() {
  const location = useLocation();

  // Scroll to the "types" grid if we arrived via #types
  useEffect(() => {
    if (location.hash === '#types') {
      const el = document.getElementById('types');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-background" data-testid="partners-landing">
      {/* HERO */}
      <section className="relative pt-28 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/40 via-fuchsia-900/30 to-amber-900/20 pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-fuchsia-500/10 via-transparent to-transparent pointer-events-none" />
        <div className="relative max-w-5xl mx-auto text-center space-y-6">
          <Badge className="bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/30 mx-auto">
            Eventism Partners
          </Badge>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold font-['Space_Grotesk'] leading-[1.05]">
            Events every weekend.
            <br />
            <span className="bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-amber-400 bg-clip-text text-transparent">
              Real revenue every week.
            </span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Eventism is Denver's end-to-end event platform — we book venues, sell tickets, run marketing, and pay you
            through escrow. Add your service once, get booked across our marketplace, planning hub, and supplier hub.
          </p>
          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <a href="#types">
              <Button size="lg" className="bg-fuchsia-600 hover:bg-fuchsia-700 h-12 px-6" data-testid="hero-browse-types">
                Pick your partner type <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </a>
            <Link to="/login">
              <Button size="lg" variant="outline" className="h-12 px-6">
                Already a partner? Sign in
              </Button>
            </Link>
          </div>
          <p className="text-xs text-muted-foreground">Free to list. You keep 100% of your rate.</p>
        </div>
      </section>

      {/* PERKS */}
      <section className="px-4 pb-20">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PERKS.map((p) => (
            <Card key={p.label} className="bg-card/80 border-border hover:border-indigo-500/40 transition-colors">
              <CardContent className="p-5 flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                  <p.icon className="h-5 w-5 text-indigo-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{p.label}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{p.sub}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* PARTNER TYPES */}
      <section id="types" className="px-4 pb-20 scroll-mt-24">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="text-center space-y-3">
            <h2 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk']">Pick your category</h2>
            <p className="text-muted-foreground">Click to start — we'll take you straight to sign-up with the right service page pre-selected.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PARTNER_TYPES.map((t) => (
              <PartnerCard key={t.key} t={t} />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="px-4 pb-20 bg-muted/30">
        <div className="max-w-5xl mx-auto py-14 space-y-10">
          <div className="text-center space-y-3">
            <h2 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk']">How it works</h2>
            <p className="text-muted-foreground">From stranger to booked in 48 hours.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="relative p-6 rounded-2xl bg-card border border-border">
                <div className="absolute -top-4 left-6 w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-white font-bold font-['Space_Grotesk']">
                  {s.n}
                </div>
                <h3 className="font-semibold text-lg mt-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 pb-12">
        <div className="max-w-3xl mx-auto text-center space-y-5">
          <Sparkles className="h-10 w-10 text-fuchsia-400 mx-auto" />
          <h2 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk']">Ready to get booked?</h2>
          <p className="text-muted-foreground">Create your free partner account and post your first service — we'll guide you through it.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#types">
              <Button size="lg" className="bg-fuchsia-600 hover:bg-fuchsia-700 h-12 px-6" data-testid="final-cta-browse">
                Get started <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* APPLY TO BE AN ORGANIZER — sits below the vendor CTA so promoters
          who came here looking for hosting tools find it without scrolling
          past the entire vendor pitch. */}
      <section className="px-4 pb-24">
        <div className="max-w-3xl mx-auto rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-900/30 via-purple-900/20 to-fuchsia-900/20 p-7 sm:p-9 text-center space-y-4" data-testid="organizer-cta-section">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-indigo-300">Are you an event organizer or promoter?</p>
          <h2 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">Run your own ticketing &amp; marketing</h2>
          <p className="text-muted-foreground text-sm max-w-xl mx-auto">
            Apply to host events under your own brand on Eventism — same low fees as Eventbrite, far better marketing tools.
            Reserved seating, waitlists, AI email/SMS blasts, two-way CRM, and a marketplace where venues find you.
          </p>
          <div className="flex flex-wrap justify-center gap-3 pt-1">
            <Button
              size="lg"
              className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white h-12 px-6"
              onClick={() => window.location.assign('/organizations/apply')}
              data-testid="apply-organizer-cta-btn"
            >
              Apply to be an Organizer <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground/80 pt-1">Approval typically within 24h · No setup fee</p>
        </div>
      </section>
    </div>
  );
}

function PartnerCard({ t }) {
  const navigate = useNavigate();
  const start = () => {
    // Preserve partner type + return path so Register lands them back on the right "add first service" page.
    const returnTo = encodeURIComponent(`${t.firstServicePath}?welcome=1&partner=${t.key}`);
    navigate(`/register?returnTo=${returnTo}&partner=${t.key}&role=${t.role}`);
  };
  return (
    <Card
      className="group cursor-pointer bg-card/80 border-border hover:border-fuchsia-500/40 transition-all overflow-hidden"
      onClick={start}
      data-testid={`partner-card-${t.key}`}
    >
      <div className={`h-20 bg-gradient-to-br ${t.color} flex items-center justify-center relative`}>
        <t.icon className="h-10 w-10 text-white/90" />
      </div>
      <CardContent className="p-4 space-y-2">
        <h3 className="font-semibold text-base font-['Space_Grotesk']">{t.label}</h3>
        <p className="text-xs text-muted-foreground line-clamp-2">{t.sample}</p>
        <div className="flex items-center gap-1 text-xs text-fuchsia-400 pt-1 group-hover:translate-x-0.5 transition-transform">
          Start free <ArrowRight className="h-3.5 w-3.5" />
        </div>
      </CardContent>
    </Card>
  );
}
